import type { Contact, ContactWithStatus } from '@/types/contacts';
import {
  normalizePublicError,
  type PublicErrorResult,
  publicErrorMessage,
  readJsonOrThrow,
} from './error';

export interface CreateContactData {
  name: string;
  type: 'email' | 'phone' | 'telegram';
  email?: string | null;
  phone_number?: string | null;
  telegram_chat_id?: string | null;
}

export interface UpdateContactData {
  name?: string;
  email?: string | null;
  phone_number?: string | null;
  telegram_chat_id?: string | null;
}

export interface VerificationError extends PublicErrorResult {
  nextAllowedAt?: string;
}

export interface VerificationSuccess {
  success: true;
  message?: string;
}

export interface TelegramLinkSuccess {
  success: true;
  message?: string;
  deepLink: string;
  expiresAt: string;
}

export interface TestMessageSuccess {
  success: true;
  channel: 'email' | 'sms' | 'telegram';
  destination: string;
  queued: true;
  message: string;
  note?: string;
}

export interface TestMessageError extends PublicErrorResult {}

export interface MonitoringEmailResubscribeRequestSuccess {
  status: 'confirmation_queued';
}

export interface MonitoringEmailResubscribeRequestError extends PublicErrorResult {}

async function readContactResponse<T>(response: Response, fallbackMessage: string): Promise<T> {
  const payload = await readJsonOrThrow(response, fallbackMessage);
  if (!response.ok) {
    throw new Error(publicErrorMessage(payload, fallbackMessage));
  }

  return payload as T;
}

async function readContactResult<T>(
  response: Response,
  fallbackMessage: string,
): Promise<T | PublicErrorResult> {
  try {
    const payload = await response.json();
    return response.ok ? (payload as T) : normalizePublicError(payload, fallbackMessage);
  } catch {
    return normalizePublicError(undefined, fallbackMessage);
  }
}

async function readContactDataResult<T>(
  response: Response,
  fallbackMessage: string,
): Promise<T | PublicErrorResult> {
  try {
    const payload = await response.json();
    if (!response.ok) return normalizePublicError(payload, fallbackMessage);

    if (
      typeof payload === 'object' &&
      payload !== null &&
      'data' in payload &&
      typeof payload.data === 'object' &&
      payload.data !== null
    ) {
      return payload.data as T;
    }
  } catch {
    // Use the stable route-specific error below.
  }

  return normalizePublicError(undefined, fallbackMessage);
}

export const contactsApi = {
  async getAll(): Promise<ContactWithStatus[]> {
    const response = await fetch('/api/contacts', {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch contacts');
    }

    return response.json();
  },

  async getById(id: number): Promise<ContactWithStatus> {
    const response = await fetch(`/api/contacts/${id}`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch contact');
    }

    return response.json();
  },

  async create(data: CreateContactData): Promise<Contact> {
    const response = await fetch('/api/contacts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    return readContactResponse(response, 'Failed to create contact');
  },

  async update(id: number, data: UpdateContactData): Promise<Contact> {
    const response = await fetch(`/api/contacts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(data),
    });

    return readContactResponse(response, 'Failed to update contact');
  },

  async delete(id: number): Promise<void> {
    const response = await fetch(`/api/contacts/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      const payload = await readJsonOrThrow(response, 'Failed to delete contact');
      throw new Error(publicErrorMessage(payload, 'Failed to delete contact'));
    }
  },

  async requestVerification(id: number): Promise<VerificationSuccess | VerificationError> {
    const response = await fetch(`/api/contacts/${id}/verify`, {
      method: 'POST',
      credentials: 'include',
    });

    return readContactResult<VerificationSuccess>(response, 'Failed to request verification');
  },

  async submitVerificationCode(
    id: number,
    code: string,
  ): Promise<VerificationSuccess | VerificationError> {
    const response = await fetch(`/api/contacts/${id}/verify/code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ code }),
    });

    return readContactResult<VerificationSuccess>(response, 'Failed to submit verification code');
  },

  async requestTelegramLink(id: number): Promise<TelegramLinkSuccess | VerificationError> {
    const response = await fetch(`/api/contacts/${id}/telegram/link`, {
      method: 'POST',
      credentials: 'include',
    });

    return readContactResult<TelegramLinkSuccess>(response, 'Failed to request Telegram link');
  },

  async sendTestMessage(id: number): Promise<TestMessageSuccess | TestMessageError> {
    const response = await fetch(`/api/contacts/${id}/test`, {
      method: 'POST',
      credentials: 'include',
    });

    return readContactResult<TestMessageSuccess>(response, 'Failed to send test message');
  },

  async requestMonitoringEmailResubscribe(
    id: number,
  ): Promise<MonitoringEmailResubscribeRequestSuccess | MonitoringEmailResubscribeRequestError> {
    const response = await fetch(`/api/contacts/${id}/monitoring-email/resubscribe-request`, {
      method: 'POST',
      credentials: 'include',
    });

    return readContactDataResult<MonitoringEmailResubscribeRequestSuccess>(
      response,
      'Failed to request monitoring email re-subscription',
    );
  },
};
